import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '../../i18n';

// ---------------------------------------------------------------------------
// ErrorBoundary — global React error boundary
// ---------------------------------------------------------------------------
// FallbackUI uses only inline styles — NO makeStyles / FluentUI hooks — so
// it can render safely even when FluentProvider is not in the tree (which is
// the case when ErrorBoundary wraps FluentProvider in App.tsx).
// ---------------------------------------------------------------------------

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '16px',
    padding: '24px',
    textAlign: 'center' as const,
    backgroundColor: '#F0F5EC',
    fontFamily: 'system-ui, sans-serif',
  },
  icon: { fontSize: '48px' },
  title: { fontSize: '20px', fontWeight: '600', margin: 0 },
  body:  { color: '#5A6472', fontSize: '14px', margin: 0 },
  detail: {
    fontFamily: 'monospace',
    fontSize: '12px',
    backgroundColor: '#e8ede4',
    padding: '12px',
    borderRadius: '6px',
    maxWidth: '600px',
    overflowX: 'auto' as const,
    textAlign: 'left' as const,
    whiteSpace: 'pre-wrap' as const,
    color: '#3a3a3a',
  },
  btn: {
    padding: '8px 24px',
    backgroundColor: '#1B7A32',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
  },
};

function FallbackUI({ error }: { error: Error }) {
  const t = useT();
  return (
    <div style={styles.wrap}>
      <div style={styles.icon}>⚠️</div>
      <p style={styles.title}>{t.errorBoundary.title}</p>
      <p style={styles.body}>{t.errorBoundary.message}</p>
      <pre style={styles.detail}>{error.message}</pre>
      <button style={styles.btn} onClick={() => window.location.reload()}>
        {t.errorBoundary.reloadBtn}
      </button>
    </div>
  );
}

interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with Sentry / CloudWatch in production
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.error) return <FallbackUI error={this.state.error} />;
    return this.props.children;
  }
}
